<style scoped lang="scss">
  label {
    @apply text-gray-900 font-semibold text-12 mb-2;
  }

  input {
    @apply h-[41px] px-4 text-14 border bg-none text-gray-900 border-gray-300 rounded-[8px] focus:border-secondary-300 transition-colors;

    &::placeholder {
      @apply text-gray-900 text-opacity-50;
    }

    &::-webkit-calendar-picker-indicator {
      transition: fill 0.2s;
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23F0D7B9" opacity="0.5" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
    }

    &:focus {
      &::-webkit-calendar-picker-indicator {
        background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 24 24"><path fill="%23E9E6E2" opacity="1" d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"/></svg>');
      }
    }
  }

  .disabled {
    input {
      @apply bg-gray-50;
    }
  }

  .hovering {
    input {
      @apply border-gray-500;
    }
    .prefix,
    .suffix {
      @apply border-gray-500;
    }
  }

  .is-focussed {
    input {
      @apply border-secondary-300;
    }
    .prefix,
    .suffix {
      @apply bg-secondary-300 border-secondary-300;
    }
  }

  .prefix,
  .suffix {
    @apply text-gray-900 text-14 transition-colors text-opacity-70;
  }

  .no-label {
    label {
      display: none;
    }
  }
</style>

<template>
  <div :class="[className, { 'disabled cursor-not-allowed': disabled, 'is-focussed': isFocussed }]">
    <label v-if="label" :for="`id_${name}`" :class="[labelClass]" class="block mb-[4px]">
      <span v-html="label"></span>
      <span
        v-if="(rules && typeof rules === 'string' && rules.includes('required') && !rules.includes('required_if')) || (typeof rules === 'object' && hasRequired) || fakeRequired"
        class="text-red"
      >
        *
      </span>
    </label>
    <div class="group relative z-0 flex items-center" :class="{ '': disabled, hovering: hovering }" @mouseover="hovering = true" @mouseleave="hovering = false">
      <span
        ref="prefixEl"
        v-if="$slots.prefix"
        class="prefix z-10 absolute border-t border-b border-l left-0 rounded-l-[8px] px-3 h-full flex items-center bg-gray-300"
        :class="{ '!bg-red !border-red !text-white': shouldShowError }"
      >
        <slot name="prefix" v-bind="{ disabled }"></slot>
      </span>
      <input
        v-model="localValue"
        autocomplete="off"
        ref="inputEl"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        :type="type"
        :class="[inputClass, 'w-full appearance-none focus:outline-none flex items-center ease-in-out', { '!border-red': shouldShowError }]"
        :style="{ 'padding-right': suffixWidth, 'padding-left': prefixWidth }"
        :id="`id_${name}`"
        :placeholder="placeholder"
        :step="step"
        :min="min"
        :max="max"
        :maxlength="maxlength"
        :disabled="disabled"
        :aria-describedby="`description_${name}`"
        :rules="rules"
      />
      <span
        ref="suffixEl"
        v-if="$slots.suffix"
        class="suffix z-10 absolute border-t border-b border-r right-0 rounded-r-[8px] px-3 h-full flex items-center bg-gray-300"
        :class="{ '!bg-red !border-red !text-white': shouldShowError }"
      >
        <slot name="suffix"></slot>
      </span>
    </div>
    <span v-if="shouldShowError" class="text-red text-12 block mt-2">{{ errorMessage }}</span>
  </div>
</template>

<script>
  import { useField } from "vee-validate";
  import { computed, ref, watch, onUpdated, onMounted } from "vue";

  export default {
    name: "FormInput",
    props: {
      name: {
        type: String,
        default: "",
      },
      value: {
        type: [String, Number],
        default: "",
      },
      placeholder: {
        type: String,
        default: "",
      },
      rules: {
        type: [String, Object],
        default: "",
      },
      label: {
        type: String,
        default: "",
      },
      step: {
        type: [Number, String],
        default: undefined,
      },
      min: {
        type: Number,
        default: undefined,
      },
      max: {
        type: Number,
        default: undefined,
      },
      maxlength: {
        type: Number,
        default: undefined,
      },
      showError: {
        type: Boolean,
        default: true,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String,
        default: "text",
      },
      autoFocus: {
        type: Boolean,
        default: false,
      },
      labelClass: {
        type: String,
        default: "text-12",
      },
      inputClass: {
        type: String,
        default: "",
      },
      className: {
        type: String,
        default: "",
      },
      fakeRequired: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["blur", "focus", "update:value"],
    setup(props, { emit }) {
      const isFocussed = ref(false);
      const hovering = ref(false);
      const prefixWidth = ref("20px");
      const suffixWidth = ref("20px");
      const prefixEl = ref(null);
      const suffixEl = ref(null);
      const inputEl = ref(null);

      // Use vee-validate's useField for validation
      const validateAs = computed(() => {
        if (props.label) {
          return props.label;
        }
        if (props.placeholder) {
          return props.placeholder;
        }
        if (props.name) {
          return props.name;
        }
        return `${props.type} field`;
      });

      const {
        value: localValue,
        errorMessage,
        meta,
        handleBlur: fieldBlur,
        setValue,
      } = useField(props.name || validateAs.value, props.rules, {
        initialValue: props.value,
        validateOnValueUpdate: true,
      });

      // Only show errors for touched fields
      const shouldShowError = computed(() => {
        return !!errorMessage.value && meta.touched;
      });

      const hasPrefix = computed(() => !!prefixEl.value);
      const hasSuffix = computed(() => !!suffixEl.value);

      const hasRequired = computed(() => {
        if (typeof props.rules === "object") {
          return !!props.rules.required;
        }
        return false;
      });
      const hasCapitalizeRule = computed(() => {
        if (typeof props.rules === "object") {
          return !!props.rules.capitalized;
        } else if (typeof props.rules === "string") {
          return props.rules.includes("capitalized");
        }
        return false;
      });

      const setPreSuffixWidth = () => {
        if (hasPrefix.value && prefixEl.value) {
          prefixWidth.value = `${prefixEl.value.offsetWidth + 16}px`;
        }
        if (hasSuffix.value && suffixEl.value) {
          suffixWidth.value = `${suffixEl.value.offsetWidth + 16}px`;
        }
      };

      const handleInput = (e) => {
        emit("input", e.target.value);
      };

      const handleBlur = (e) => {
        isFocussed.value = false;
        fieldBlur(); // This sets meta.touched = true
        emit("blur", e);
      };

      const handleFocus = (e) => {
        isFocussed.value = true;
        // Remove content selection on focus for better user experience
        emit("focus", e);
      };

      // This watches for external changes to value
      watch(
        () => props.value,
        (newValue) => {
          if (String(newValue) !== String(localValue.value)) {
            setValue(newValue);
          }
        },
        { immediate: true },
      );

      const capitalizeFirstLetter = (string) => {
        return string.charAt(0)?.toUpperCase() + string.slice(1);
      };
      // This emits changes from the input to the parent
      watch(localValue, (newValue) => {
        if (hasCapitalizeRule.value) {
          newValue = capitalizeFirstLetter(newValue);
        }
        emit("update:value", newValue);
        emit("input", newValue);
      });

      onUpdated(() => {
        setPreSuffixWidth();
      });

      // Initialize the width calculation on first render
      setPreSuffixWidth();

      onMounted(() => {
        if (props.autoFocus) {
          inputEl.value.focus();
        }
      });

      return {
        localValue,
        errorMessage,
        meta,
        isFocussed,
        hovering,
        prefixWidth,
        suffixWidth,
        prefixEl,
        suffixEl,
        inputEl,
        validateAs,
        hasPrefix,
        hasSuffix,
        hasRequired,
        handleBlur,
        handleFocus,
        handleInput,
        shouldShowError,
      };
    },
  };
</script>
